import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

export interface SocialVideo {
  id: string;
  plataforma: 'tiktok' | 'instagram';
  url: string;
  titulo: string;
}

const saveSettings = async (updates: { key: string; value: unknown }[]) => {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/bali-save-settings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
    body: JSON.stringify({ updates }),
  });
  return res.ok;
};

const getEmbedUrl = (plataforma: 'tiktok' | 'instagram', url: string): string => {
  if (plataforma === 'tiktok') {
    // https://www.tiktok.com/@user/video/123456789
    const match = url.match(/\/video\/(\d+)/);
    if (match) return `https://www.tiktok.com/embed/v2/${match[1]}`;
    return '';
  }
  if (plataforma === 'instagram') {
    // https://www.instagram.com/reel/ABC123/ or /p/ABC123/
    const match = url.match(/\/(reel|p)\/([A-Za-z0-9_-]+)/);
    if (match) return `https://www.instagram.com/p/${match[2]}/embed`;
    return '';
  }
  return '';
};

const platformConfig = {
  tiktok:    { label: 'TikTok',    icon: 'ri-tiktok-line',    color: 'text-white',    bg: 'bg-zinc-800' },
  instagram: { label: 'Instagram', icon: 'ri-instagram-line', color: 'text-pink-400', bg: 'bg-pink-500/10' },
};

const SocialTab = () => {
  const [videos, setVideos] = useState<SocialVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [preview, setPreview] = useState<SocialVideo | null>(null);
  const [newVideo, setNewVideo] = useState<{ plataforma: 'tiktok' | 'instagram'; url: string; titulo: string }>({
    plataforma: 'tiktok', url: '', titulo: '',
  });
  const [addError, setAddError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('TALLERMECANICOKEVIN_settings')
        .select('value')
        .eq('key', 'social_videos')
        .maybeSingle();
      if (data?.value) setVideos(data.value as SocialVideo[]);
      setLoading(false);
    };
    load();
  }, []);

  const handleAdd = async () => {
    setAddError('');
    if (!newVideo.url.trim()) { setAddError('Añade el enlace del vídeo.'); return; }
    const embedUrl = getEmbedUrl(newVideo.plataforma, newVideo.url.trim());
    if (!embedUrl) { setAddError('No se pudo extraer el ID del vídeo. Comprueba el enlace.'); return; }
    const video: SocialVideo = {
      id: `sv-${Date.now()}`,
      plataforma: newVideo.plataforma,
      url: newVideo.url.trim(),
      titulo: newVideo.titulo.trim() || `Vídeo de ${platformConfig[newVideo.plataforma].label}`,
    };
    const updated = [...videos, video];
    setVideos(updated);
    setNewVideo({ plataforma: 'tiktok', url: '', titulo: '' });
    setSaving(true);
    await saveSettings([{ key: 'social_videos', value: updated }]);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleRemove = async (id: string) => {
    if (preview?.id === id) setPreview(null);
    const updated = videos.filter((v) => v.id !== id);
    setVideos(updated);
    setSaving(true);
    await saveSettings([{ key: 'social_videos', value: updated }]);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleSave = async () => {
    setSaving(true);
    await saveSettings([{ key: 'social_videos', value: videos }]);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-40">
      <i className="ri-loader-4-line animate-spin text-yellow-400 text-2xl" />
    </div>
  );

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white font-bold text-sm uppercase tracking-wider">Vídeos Sociales</h2>
          <p className="text-zinc-500 text-xs mt-0.5">Añade vídeos de TikTok e Instagram para mostrar en la web</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className={`flex items-center gap-1.5 px-4 py-2 text-xs font-black uppercase tracking-wider cursor-pointer whitespace-nowrap transition-all disabled:opacity-60 ${saved ? 'bg-green-500 text-white' : 'bg-yellow-400 hover:bg-yellow-300 text-black'}`}
        >
          <i className={saving ? 'ri-loader-4-line animate-spin' : saved ? 'ri-check-line' : 'ri-save-line'} />
          {saving ? 'Guardando…' : saved ? '¡Guardado!' : 'Guardar cambios'}
        </button>
      </div>

      {/* Añadir vídeo */}
      <section className="bg-zinc-900 border border-zinc-800 p-5">
        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-zinc-800">
          <div className="w-6 h-6 flex items-center justify-center text-yellow-400">
            <i className="ri-add-circle-line text-base" />
          </div>
          <h3 className="text-white font-bold text-sm uppercase tracking-wider">Añadir vídeo</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          {/* Plataforma */}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5">Plataforma</label>
            <div className="flex gap-2">
              {(['tiktok', 'instagram'] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setNewVideo((prev) => ({ ...prev, plataforma: p }))}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-bold uppercase tracking-wide border transition-all cursor-pointer whitespace-nowrap ${
                    newVideo.plataforma === p
                      ? 'border-yellow-400 bg-yellow-400/10 text-yellow-400'
                      : 'border-zinc-700 bg-zinc-800 text-zinc-400 hover:border-zinc-600'
                  }`}
                >
                  <i className={platformConfig[p].icon} />
                  {platformConfig[p].label}
                </button>
              ))}
            </div>
          </div>

          {/* URL */}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5">Enlace del vídeo</label>
            <input
              value={newVideo.url}
              onChange={(e) => setNewVideo((p) => ({ ...p, url: e.target.value }))}
              placeholder={newVideo.plataforma === 'tiktok' ? 'https://www.tiktok.com/@user/video/...' : 'https://www.instagram.com/reel/...'}
              className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-3 py-2 outline-none focus:border-yellow-400/50 transition-colors placeholder:text-zinc-600"
            />
          </div>

          {/* Título */}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5">Título (opcional)</label>
            <input
              value={newVideo.titulo}
              onChange={(e) => setNewVideo((p) => ({ ...p, titulo: e.target.value }))}
              placeholder="Ej: Cambio de frenos rápido…"
              className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-3 py-2 outline-none focus:border-yellow-400/50 transition-colors placeholder:text-zinc-600"
            />
          </div>
        </div>

        {addError && (
          <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 text-red-400 text-xs mb-3">
            <i className="ri-error-warning-line" /> {addError}
          </div>
        )}

        <button
          onClick={handleAdd}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-black uppercase tracking-wider cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
        >
          <i className={saving ? 'ri-loader-4-line animate-spin' : 'ri-add-line'} />
          {saving ? 'Guardando…' : 'Añadir y guardar'}
        </button>
      </section>

      {/* Lista de vídeos */}
      <section className="bg-zinc-900 border border-zinc-800 p-5">
        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-zinc-800">
          <div className="w-6 h-6 flex items-center justify-center text-yellow-400">
            <i className="ri-video-line text-base" />
          </div>
          <h3 className="text-white font-bold text-sm uppercase tracking-wider">
            Vídeos añadidos
          </h3>
          <span className="ml-auto text-zinc-600 text-xs">{videos.length} vídeo{videos.length !== 1 ? 's' : ''}</span>
        </div>

        {videos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-12 h-12 flex items-center justify-center text-zinc-700 mb-3">
              <i className="ri-video-off-line text-3xl" />
            </div>
            <p className="text-zinc-600 text-sm">Sin vídeos añadidos todavía</p>
            <p className="text-zinc-700 text-xs mt-1">Añade tu primer vídeo de TikTok o Instagram arriba</p>
          </div>
        ) : (
          <div className="space-y-2">
            {videos.map((v) => {
              const cfg = platformConfig[v.plataforma];
              const embedUrl = getEmbedUrl(v.plataforma, v.url);
              return (
                <div key={v.id} className="flex items-center gap-3 bg-zinc-800/60 border border-zinc-700 px-4 py-3">
                  {/* Platform badge */}
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 ${cfg.bg}`}>
                    <i className={`${cfg.icon} text-sm ${cfg.color}`} />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{v.titulo}</p>
                    <p className="text-zinc-500 text-xs truncate">{v.url}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {embedUrl && (
                      <button
                        onClick={() => setPreview(preview?.id === v.id ? null : v)}
                        className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold uppercase tracking-wide border cursor-pointer whitespace-nowrap transition-all ${
                          preview?.id === v.id
                            ? 'border-yellow-400/50 bg-yellow-400/10 text-yellow-400'
                            : 'border-zinc-600 bg-zinc-700 text-zinc-300 hover:border-zinc-500'
                        }`}
                      >
                        <i className={preview?.id === v.id ? 'ri-eye-off-line' : 'ri-eye-line'} />
                        {preview?.id === v.id ? 'Ocultar' : 'Preview'}
                      </button>
                    )}
                    <a
                      href={v.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-8 h-8 border border-zinc-600 bg-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-500 cursor-pointer transition-colors"
                    >
                      <i className="ri-external-link-line text-sm" />
                    </a>
                    <button
                      onClick={() => handleRemove(v.id)}
                      className="flex items-center justify-center w-8 h-8 border border-zinc-700 text-zinc-600 hover:text-red-400 hover:border-red-500/40 cursor-pointer transition-colors"
                    >
                      <i className="ri-delete-bin-line text-sm" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Preview inline */}
      {preview && (
        <section className="bg-zinc-900 border border-yellow-400/30 p-5">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-zinc-800">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 flex items-center justify-center text-yellow-400">
                <i className="ri-play-circle-line text-base" />
              </div>
              <h3 className="text-white font-bold text-sm uppercase tracking-wider">Vista previa: {preview.titulo}</h3>
            </div>
            <button
              onClick={() => setPreview(null)}
              className="w-7 h-7 flex items-center justify-center text-zinc-500 hover:text-white cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-base" />
            </button>
          </div>
          <div className="flex justify-center">
            <iframe
              src={getEmbedUrl(preview.plataforma, preview.url)}
              className="w-full max-w-sm border border-zinc-700"
              style={{ height: preview.plataforma === 'tiktok' ? 720 : 540 }}
              allow="autoplay; encrypted-media"
              allowFullScreen
              title={preview.titulo}
            />
          </div>
        </section>
      )}
    </div>
  );
};

export default SocialTab;
export { getEmbedUrl };
